import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import {
  WorkflowDefinitionDto,
  WorkflowHeaderButtonDto,
  WorkflowListItemDto,
  WorkflowPublicConfigDto,
} from '@src/app/core/models/workflow/workflow-definition.model';
import { PagedResponse } from '@src/app/core/models/shared/paged-response.model';

/** Payload for PUT .../stages (backend SetWorkflowStagesCommand). */
export interface WorkflowStagesUpdatePayload {
  /** Botões dinâmicos no cabeçalho do formulário (opcional). */
  headerButtons?: WorkflowHeaderButtonDto[];
  /** Formulário acessível sem autenticação. */
  isPublic?: boolean;
  /** Exige CAPTCHA na criação/envio em formulários públicos. */
  requireCaptcha?: boolean;
  stages: {
    id: number;
    order: number;
    title: string;
    description?: string;
    fieldActionsJson?: string;
    fields: WorkflowStageFieldPayload[];
    fieldGroups?: WorkflowStageFieldGroupPayload[];
    approverTeamIds: number[];
    approverUserIds: number[];
    onApprovalNextStageOrder: number | null;
    onRejectionNextStageOrder: number | null;
    rejectionAction: number;
    returnToStageOrder: number | null;
    fieldReviewMode?: number;
    showRejectionReasonToAuthor?: boolean;
    requireJustification?: boolean;
    allowFieldRejection?: boolean;
    allowApproverEditFields?: boolean;
  }[];
}

export interface WorkflowStageFieldPayload {
  id: number;
  workflowStageFieldGroupId?: number | null;
  groupKey?: string;
  order: number;
  row: number;
  colStart: number;
  colSpan: number;
  schemaFieldKey: string;
  labelKey: string;
  placeholderKey?: string;
  isRequired: boolean;
  maxLength?: number;
  validationRegex?: string;
  customValidationExpression?: string;
  inputMask?: string;
  fieldType: string;
  inputType?: string;
  dataSourceType: number;
  dataSourceConfigJson?: string;
  customPropertiesJson?: string;
}

export interface WorkflowStageFieldGroupPayload {
  id: number;
  groupKey: string;
  title: string;
  entityKey?: string;
  order: number;
  minRows: number;
  maxRows: number;
  layout: string;
  configJson?: string;
  fields: WorkflowStageFieldPayload[];
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowDefinitionService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/workflow-definitions`;

  getList(options?: {
    title?: string;
    isActive?: boolean;
    sortColumn?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    targetTableName?: string;
  }): Observable<PagedResponse<WorkflowListItemDto>> {
    let params = new HttpParams();
    if (options?.title) params = params.set('title', options.title);
    if (options?.isActive !== undefined)
      params = params.set('isActive', options.isActive);
    if (options?.sortColumn)
      params = params.set('sortColumn', options.sortColumn);
    if (options?.sortOrder)
      params = params.set('sortOrder', options.sortOrder);
    if (options?.page) params = params.set('page', options.page);
    if (options?.pageSize) params = params.set('pageSize', options.pageSize);
    if (options?.targetTableName)
      params = params.set('targetTableName', options.targetTableName);

    return this.http.get<PagedResponse<WorkflowListItemDto>>(
      this.baseUrl,
      { params }
    );
  }

  getById(id: number): Observable<WorkflowDefinitionDto> {
    return this.http.get<WorkflowDefinitionDto>(`${this.baseUrl}/${id}/full`);
  }

  /** Configuração pública por slug (acesso anônimo). Retorna null se inativo ou inexistente (404). */
  getPublicConfig(slug: string): Observable<WorkflowPublicConfigDto | null> {
    return this.http
      .get<WorkflowPublicConfigDto>(`${this.baseUrl}/public-config/${encodeURIComponent(slug)}`)
      .pipe(catchError(() => of(null)));
  }

  /** Definição completa por slug apenas para workflows públicos e ativos (acesso anônimo). */
  getFullBySlugForPublic(slug: string): Observable<WorkflowDefinitionDto> {
    return this.http.get<WorkflowDefinitionDto>(`${this.baseUrl}/public/${encodeURIComponent(slug)}/full`);
  }

  create(payload: { title?: string; description?: string; slug?: string; isActive?: boolean }): Observable<number> {
    return this.http
      .post<{ id: number }>(this.baseUrl, payload)
      .pipe(map((r) => r?.id ?? 0));
  }

  update(id: number, payload: Partial<WorkflowDefinitionDto>): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, payload);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  toggleActive(id: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/toggle-active`, {});
  }

  updateStages(id: number, payload: WorkflowStagesUpdatePayload, saveAsDraft = false): Observable<void> {
    let params = new HttpParams();
    if (saveAsDraft) params = params.set('saveAsDraft', 'true');
    return this.http.put<void>(`${this.baseUrl}/${id}/stages`, payload, { params });
  }

  /**
   * Atualiza etapas/aprovadores/propriedades dos campos sem alterar o conjunto de campos (workflow com instâncias).
   */
  updateStagesRestricted(id: number, payload: WorkflowStagesUpdatePayload, saveAsDraft = false): Observable<void> {
    let params = new HttpParams();
    if (saveAsDraft) params = params.set('saveAsDraft', 'true');
    return this.http.put<void>(`${this.baseUrl}/${id}/stages-restricted`, payload, { params });
  }

  /** Atualiza apenas o helper de um campo (permitido quando o workflow já tem instâncias). */
  updateFieldHelper(
    workflowId: number,
    stageOrder: number,
    schemaFieldKey: string,
    helper: { enabled: boolean; content: string; icon?: string; position?: string }
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${workflowId}/stages/fields/helper`, {
      stageOrder,
      schemaFieldKey,
      helper,
    });
  }

  clone(id: number, inactivateSource = true): Observable<number> {
    return this.http
      .post<{ id: number }>(`${this.baseUrl}/${id}/clone`, { inactivateSource })
      .pipe(map((r) => r?.id ?? 0));
  }
}

